<template>
  <div id="association-section">
    <section-header title="Customer Relationships" :allowCollapse="false" parentSelector="#association-section"></section-header>

    <div class="box" style="padding: 0.75rem 1rem; margin-bottom: 0.75rem;">
      <div class="columns is-multiline" style="margin-bottom: 0;">
        <div class="column is-4">
          <customer-selector
            label="Customer"
            v-model="selectedCustomer"
            :required="false"
            :allowNew="true"
            :allowEdit="false"
            :showRelatedCustomers="false"
            :showNewAction="false"
            :relatedCustomer="true"
            :contactData="parentContactData"
            :useValidator="this.$validator"
            instanceName="association-target-customer">
          </customer-selector>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label">Relationship</label>
            <multiselect
              :showLabels="false"
              :searchable="true"
              :allow-empty="true"
              v-model="selectedTypeOption"
              :options="associationTypeOptions"
              label="altName"
              trackBy="name"
              placeholder="Select relationship">
            </multiselect>
          </div>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label">Notes</label>
            <div class="control">
              <input class="input" v-model="newNotes" placeholder="Optional" maxlength="500">
            </div>
          </div>
        </div>
        <div class="column is-2">
          <div class="field">
            <label class="label">&nbsp;</label>
            <a class="button is-primary" :class="{'is-loading': isAdding}" :disabled="!canAdd" @click="addAssociation">
              <span class="icon"><i class="fas fa-plus"></i></span>
              <span>Add</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <b-table :data="associations" :loading="isLoading" class="lists" narrowed hoverable>
      <template slot-scope="props">
        <b-table-column field="targetCustomerName" label="Customer">
          <b-tooltip :label="props.row.notes" position="is-right" type="is-dark" v-if="props.row.notes">
            <a @click="viewCustomer(props.row.targetCustomerId)" class="has-text-link">
              {{ props.row.targetCustomerName }}
            </a>
          </b-tooltip>
          <a v-else @click="viewCustomer(props.row.targetCustomerId)" class="has-text-link">
            {{ props.row.targetCustomerName }}
          </a>
        </b-table-column>
        <b-table-column field="associationTypeDescription" label="Relationship" width="160">
          <b-tag :type="getTagType(props.row.targetCustomerType)" style="min-width: 90px; text-align: center;">
            {{ props.row.associationTypeDescription }}
          </b-tag>
        </b-table-column>
        <b-table-column field="targetCustomerType" label="Type" width="100">
          <span class="is-size-7">{{ props.row.targetCustomerType }}</span>
        </b-table-column>
        <b-table-column field="targetCustomerEmail" label="Email">
          <span class="is-size-7">{{ props.row.targetCustomerEmail }}</span>
        </b-table-column>
        <b-table-column field="targetCustomerMobile" label="Phone" width="160">
          <click-to-call v-if="props.row.targetCustomerMobile" :destinationLineProp="props.row.targetCustomerMobile" :receiverIdProp="props.row.targetCustomerId" :customerName="props.row.targetCustomerName"></click-to-call>
          <click-to-call v-else-if="props.row.targetCustomerOffice" :destinationLineProp="props.row.targetCustomerOffice" :receiverIdProp="props.row.targetCustomerId" :customerName="props.row.targetCustomerName"></click-to-call>
        </b-table-column>
        <b-table-column label="" width="50">
          <a class="has-text-danger" @click.stop.prevent="confirmRemove(props.row)" title="Remove relationship">
            <i class="fas fa-trash-alt fa-sm"></i>
          </a>
        </b-table-column>
      </template>

      <template slot="empty">
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>No relationships found.</p>
          </div>
        </section>
      </template>
    </b-table>

    <confirm-modal
      :active="showRemoveConfirm"
      title="Remove Relationship"
      :message="removeConfirmMessage"
      confirmText="Remove"
      confirmType="is-danger"
      :isConfirming="isRemoving"
      @confirm="doRemove"
      @cancel="showRemoveConfirm = false">
    </confirm-modal>
  </div>
</template>

<script>
import customerAssociationService from '@/services/customerAssociationService'
import SectionHeader from '@/components/generic/SectionHeader'
import ClickToCallComponent from '@/components/generic/ClickToCallComponent'
import ConfirmModal from '@/components/generic/ConfirmModal'
import { createNamespacedHelpers } from 'vuex'
const mapConfigGetters = createNamespacedHelpers('config').mapGetters

export default {
  name: 'CustomerAssociationManager',
  provide () {
    return {
      $validator: this.$validator
    }
  },  
  components: {
    SectionHeader,
    'click-to-call': ClickToCallComponent,
    'confirm-modal': ConfirmModal,
    'customer-selector': () => import('@/components/generic/CustomerSelector')
  },
  props: {
    customerId: { type: Number, required: true },
    dealerId: { type: Number, required: true },
    subscriberId: { type: Number, required: true },
    parentEmail: { type: String, default: '' },
    parentMobile: { type: String, default: '' },
    customerType: { type: String, default: 'Individual' },
    useValidator: { type: Object, default: null },
  },
  data () {
    return {
      associations: [],
      isLoading: false,
      isAdding: false,
      selectedCustomer: null,
      selectedTypeOption: null,
      newNotes: '',
      showRemoveConfirm: false,
      removeConfirmMessage: '',
      removeAssociationId: null,
      isRemoving: false
    }
  },
  computed: {
    ...mapConfigGetters(['definedTypes']),
    isCompany () {
      return this.customerType === 'Company'
    },
    companyExcludedTypes () {
      return ['Spouse', 'Parent', 'Child', 'Sibling', 'Grandparent', 'Grandchild', 'Guardian', 'Ward']
    },
    individualExcludedTypes () {
      return ['Member']
    },
    associationTypeOptions () {
      if (!this.definedTypes || !this.definedTypes.customerAssociationTypes || !this.definedTypes.customerAssociationTypes.options) {
        return []
      }
      var allOptions = this.definedTypes.customerAssociationTypes.options
      if (this.isCompany) {
        return allOptions.filter(o => !this.companyExcludedTypes.includes(o.name))
      }
      return allOptions.filter(o => !this.individualExcludedTypes.includes(o.name))
    },
    parentContactData () {
      return { email: this.parentEmail, mobile: this.parentMobile }
    },
    canAdd () {
      return this.selectedCustomer && this.selectedTypeOption && !this.isAdding
    }
  },
  watch: {
    customerId: {
      immediate: true,
      handler (val) {
        if (val && val > 0) {
          this.loadAssociations()
        }
      }
    }
  },
  methods: {
    async loadAssociations () {
      if (!this.customerId || this.customerId <= 0) return
      this.isLoading = true
      try {
        this.associations = await customerAssociationService.list(this.customerId)
      } catch (e) {
        this.$buefy.toast.open({ message: 'Failed to load relationships', type: 'is-danger' })
      } finally {
        this.isLoading = false
      }
    },
    async addAssociation () {
      this.isAdding = true
      try {
        await customerAssociationService.add({
          sourceCustomerId: this.customerId,
          targetCustomerId: this.selectedCustomer.id,
          associationType: this.selectedTypeOption.name,
          notes: this.newNotes,
          dealerId: this.dealerId,
          subscriberId: this.subscriberId
        })
        this.selectedCustomer = null
        this.selectedTypeOption = null
        this.newNotes = ''
        await this.loadAssociations()
        this.$emit('association-changed')
        this.$buefy.toast.open({ message: 'Relationship added', type: 'is-success' })
      } catch (e) {
        const msg = (e && e.error) ? e.error : 'Failed to add relationship'
        this.$buefy.toast.open({ message: msg, type: 'is-danger' })
      } finally {
        this.isAdding = false
      }
    },
    confirmRemove (association) {
      this.removeAssociationId = association.id
      this.removeConfirmMessage = 'Remove relationship with <b>' + association.targetCustomerName + '</b>? This will also remove the inverse relationship.'
      this.showRemoveConfirm = true
    },
    async doRemove () {
      this.isRemoving = true
      try {
        await customerAssociationService.remove(this.removeAssociationId)
        this.showRemoveConfirm = false
        await this.loadAssociations()
        this.$emit('association-changed')
        this.$buefy.toast.open({ message: 'Relationship removed', type: 'is-success' })
      } catch (e) {
        this.$buefy.toast.open({ message: 'Failed to remove relationship', type: 'is-danger' })
      } finally {
        this.isRemoving = false
        this.removeAssociationId = null
      }
    },
    getTagType (customerType) {
      return customerType === 'Company' ? 'is-link' : 'is-primary is-light'
    },
    viewCustomer (id) {
      this.$router.push({ name: 'EditCustomer', params: { id: id } })
    }
  },
  created: function () {
    if (this.useValidator) {
      this.$validator = this.useValidator
    } else {
      this.$validator = this.$parent.$validator
    }
  }
}
</script>
